import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../../utils/axiosConfig'

const UserNotifications = () => {
  const navigate = useNavigate()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)
  const [unreadCount, setUnreadCount] = useState(0)
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })
  const [actionLoading, setActionLoading] = useState({})
  const [rejectModal, setRejectModal] = useState({ show: false, requestId: null, notificationId: null })
  const [rejectReason, setRejectReason] = useState('')

  useEffect(() => {
    loadNotifications()
  }, [])

  const loadNotifications = async (page = 1) => {
    try {
      setLoading(true)
      const response = await api.get(`/user/notifications?page=${page}`)
      const data = response.data

      if (data.notifications) {
        setNotifications(data.notifications.data || [])
        setPagination({
          current_page: data.notifications.current_page,
          last_page: data.notifications.last_page,
          total: data.notifications.total
        })
        setUnreadCount(data.unread_count || 0)
      }
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleMarkAsRead = async (id) => {
    try {
      await api.post(`/user/notifications/${id}/read`)
      setNotifications(prev => prev.map(n =>
        n.id === id ? { ...n, is_read: true, read_at: new Date().toISOString() } : n
      ))
      setUnreadCount(prev => Math.max(0, prev - 1))
      // Notify sidebar to update badge count immediately
      window.dispatchEvent(new Event('notifications-updated'))
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await api.post('/user/notifications/read-all')
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() })))
      setUnreadCount(0)
      // Notify sidebar to update badge count immediately
      window.dispatchEvent(new Event('notifications-updated'))
    } catch (error) {
      console.error('Error marking all as read:', error)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this notification?')) return

    try {
      await api.delete(`/user/notifications/${id}`)
      loadNotifications(pagination.current_page)
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const handleApproveRequest = async (requestId, notificationId) => {
    if (!confirm('Are you sure you want to approve this player registration?')) return
    try {
      setActionLoading(prev => ({ ...prev, [notificationId]: 'approving' }))
      const res = await api.post(`/user/registration-requests/${requestId}/approve`)
      if (res.data.success) {
        alert(`Player "${res.data.data?.full_name || ''}" has been registered successfully!`)
        // Mark notification as read and update its data
        setNotifications(prev => prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString(), _action: 'approved' } : n
        ))
        window.dispatchEvent(new Event('notifications-updated'))
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to approve request')
    } finally {
      setActionLoading(prev => ({ ...prev, [notificationId]: null }))
    }
  }

  const handleRejectRequest = async () => {
    const { requestId, notificationId } = rejectModal
    try {
      setActionLoading(prev => ({ ...prev, [notificationId]: 'rejecting' }))
      const res = await api.post(`/user/registration-requests/${requestId}/reject`, { reason: rejectReason })
      if (res.data.success) {
        alert('Registration request has been rejected.')
        setNotifications(prev => prev.map(n =>
          n.id === notificationId ? { ...n, is_read: true, read_at: new Date().toISOString(), _action: 'rejected' } : n
        ))
        window.dispatchEvent(new Event('notifications-updated'))
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reject request')
    } finally {
      setActionLoading(prev => ({ ...prev, [notificationId]: null }))
      setRejectModal({ show: false, requestId: null, notificationId: null })
      setRejectReason('')
    }
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now - date

    // Less than 1 hour
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000)
      return mins <= 1 ? 'Just now' : `${mins} minutes ago`
    }

    // Less than 24 hours
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000)
      return hours === 1 ? '1 hour ago' : `${hours} hours ago`
    }

    // Less than 7 days
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000)
      return days === 1 ? 'Yesterday' : `${days} days ago`
    }

    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const getTypeIcon = (type) => {
    switch (type) {
      case 'tournament_edit': return 'fas fa-edit'
      case 'tournament_publish': return 'fas fa-eye'
      case 'tournament_unpublish': return 'fas fa-eye-slash'
      case 'player_registration_request': return 'fas fa-user-plus'
      case 'error': return 'fas fa-exclamation-circle'
      case 'warning': return 'fas fa-exclamation-triangle'
      case 'success': return 'fas fa-check-circle'
      case 'info': return 'fas fa-info-circle'
      default: return 'fas fa-bell'
    }
  }

  const getTypeColor = (type) => {
    switch (type) {
      case 'tournament_edit': return '#3b82f6'
      case 'tournament_publish': return '#10b981'
      case 'tournament_unpublish': return '#f59e0b'
      case 'player_registration_request': return '#8b5cf6'
      case 'error': return '#dc2626'
      case 'warning': return '#f59e0b'
      case 'success': return '#10b981'
      case 'info': return '#3b82f6'
      default: return '#6b7280'
    }
  }

  return (
    <div className="user-notifications" style={{ padding: '2rem', maxWidth: '1200px' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
        borderRadius: '12px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: 'white' }}>
            <i className="fas fa-bell" style={{ marginRight: '10px' }}></i>
            Notifications
          </h1>
          <p style={{ margin: '0.5rem 0 0', opacity: 0.9, fontSize: '0.9rem', color: 'white' }}>
            Stay updated with tournament changes
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                color: 'white',
                fontSize: '0.85rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <i className="fas fa-check-double"></i>
              Mark all as read
            </button>
          )}
          <button
            onClick={() => navigate('/user/dashboard')}
            style={{
              background: 'white',
              border: 'none',
              color: '#c00f15',
              borderRadius: '8px',
              padding: '10px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '0.85rem',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
            }}
          >
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>
      </div>

      {/* Unread Badge */}
      {unreadCount > 0 && (
        <div style={{
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          padding: '0.75rem 1rem',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#991b1b'
        }}>
          <i className="fas fa-bell"></i>
          <span>You have <strong>{unreadCount}</strong> unread notification{unreadCount > 1 ? 's' : ''}</span>
        </div>
      )}

      {/* Notifications List */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        overflow: 'hidden'
      }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '2rem', marginBottom: '1rem' }}></i>
            <p>Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
            <i className="fas fa-bell-slash" style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}></i>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.1rem', fontWeight: '600', color: '#1f2937' }}>
              No notifications yet
            </h3>
            <p style={{ margin: 0 }}>You'll be notified when tournaments are updated</p>
          </div>
        ) : (
          <div>
            {notifications.map(notification => (
              <div
                key={notification.id}
                style={{
                  padding: '1.5rem 1.5rem',
                  borderBottom: '1px solid #f3f4f6',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  background: notification.is_read ? 'white' : '#fafafa',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
              >
                <div style={{
                  width: '44px',
                  height: '44px',
                  borderRadius: '12px',
                  background: `${getTypeColor(notification.type)}15`,
                  color: getTypeColor(notification.type),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  position: 'relative'
                }}>
                  <i className={getTypeIcon(notification.type)} style={{ fontSize: '1.1rem' }}></i>
                  {!notification.is_read && (
                    <span style={{
                      position: 'absolute',
                      top: '-2px',
                      right: '-2px',
                      width: '10px',
                      height: '10px',
                      background: '#c00f15',
                      borderRadius: '50%',
                      border: '2px solid white'
                    }}></span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <h4 style={{
                      margin: 0,
                      fontSize: '0.95rem',
                      fontWeight: notification.is_read ? '500' : '600',
                      color: '#1f2937'
                    }}>
                      {notification.title}
                    </h4>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                      <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>
                        {formatDate(notification.created_at)}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          cursor: 'pointer',
                          padding: '4px',
                          borderRadius: '4px'
                        }}
                        title="Delete"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  </div>
                  <p style={{
                    margin: '0.75rem 0 0',
                    fontSize: '0.85rem',
                    color: notification.is_read ? '#9ca3af' : '#6b7280',
                    lineHeight: 1.6
                  }}>
                    {notification.message}
                  </p>
                  {notification.event && (
                    <div style={{
                      marginTop: '0.5rem',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '4px 10px',
                      background: '#f3f4f6',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      color: '#4b5563'
                    }}>
                      <i className="fas fa-calendar"></i>
                      {notification.event.event_name}
                    </div>
                  )}

                  {/* Registration requests are reviewed by the admin — informational only for the user */}
                  {notification.type === 'player_registration_request' && (
                    <div style={{ marginTop: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.8rem', fontWeight: 600, color: '#92400e', background: '#fef3c7', padding: '6px 12px', borderRadius: '20px' }}>
                      <i className="fas fa-hourglass-half"></i> Awaiting admin review
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.last_page > 1 && (
          <div style={{
            padding: '1rem',
            display: 'flex',
            justifyContent: 'center',
            gap: '0.5rem',
            borderTop: '1px solid #e5e7eb'
          }}>
            {Array.from({ length: pagination.last_page }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => loadNotifications(page)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '6px',
                  border: page === pagination.current_page ? '2px solid #c00f15' : '1px solid #e5e7eb',
                  background: page === pagination.current_page ? '#fef2f2' : 'white',
                  color: page === pagination.current_page ? '#c00f15' : '#4b5563',
                  fontWeight: page === pagination.current_page ? '600' : '400',
                  cursor: 'pointer'
                }}
              >
                {page}
              </button>
            ))}
          </div>
        )}
      </div>
      {/* Reject Reason Modal */}
      {rejectModal.show && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }} onClick={() => setRejectModal({ show: false, requestId: null, notificationId: null })}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '1.5rem', width: '90%', maxWidth: '450px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1.1rem', color: '#1f2937' }}>
              <i className="fas fa-times-circle" style={{ color: '#dc2626', marginRight: '8px' }}></i>
              Reject Registration Request
            </h3>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection (optional)"
              style={{ width: '100%', minHeight: '100px', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem', resize: 'vertical', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <button onClick={() => { setRejectModal({ show: false, requestId: null, notificationId: null }); setRejectReason('') }} style={{ padding: '8px 20px', border: '1px solid #d1d5db', borderRadius: '6px', background: 'white', cursor: 'pointer', fontSize: '0.85rem' }}>Cancel</button>
              <button onClick={handleRejectRequest} disabled={!!actionLoading[rejectModal.notificationId]} style={{ padding: '8px 20px', border: 'none', borderRadius: '6px', background: '#dc2626', color: 'white', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}>
                {actionLoading[rejectModal.notificationId] === 'rejecting' ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserNotifications
